"use client";
import React, { useMemo, useState } from "react";
import HeaderTopBar from "./AlertBar";
import useMediaQuery from "@/utils/hooks/useMediaQuery";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import useInputIntent from "@/utils/hooks/useInputIntent";
import { BB_OFFER_TYPE, GLOBAL_SEARCH_QUERY_PARAM_KEY } from "@/utils/constant";
import {
  useAppSelector,
  useEffect,
  useAppDispatch,
} from "@/utils/hooks/reactUseCustomHooks";
import {
  addOffersToList,
  deleteOffersFromList,
  fetchMulesoftOffers,
  getOffersFromList,
  mulesoftLoadOffersService,
  mulesoftMemberInquireService,
} from "@/services/mulesoft";
import ToastComponent from "../Toast/Toast";
import { setSceneContent } from "@/utils/redux/scene/sceneContentSlice";
import {
  resetLoadedOffers,
  setHashOffers,
  setLoadedOffers,
  setLoadingOffers,
} from "@/utils/redux/fetchOffers";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { AemSearchDropDown, AemSearchResults } from "@/utils/types/aem";
import { handleEmailLogin } from "@/helpers/useCommonFunctions";
import { setFavoirateOffers } from "@/utils/redux/favOffers";
import { setAlert } from "@/utils/redux/gigya/scene";
import { getCookie, setCookie, deleteCookie, getCookies } from "cookies-next";
import { destroyCartDB } from "@/lib/indexedDb/product";
import HeaderTopSection from "./HeaderTopSection";
import DesktopNavigationBar from "./DesktopNavigationBar";
import MobileNavigationBar from "./MobileNavigationBar";
import LoadedOffersToast from "./LoadedOffersToast";

const COOKIE_RESET_KEY = "cookieResetKey";
const CART_RESET_KEY = "cartResetKey";

const Header = ({
  headerData,
  searchStaticAEMData,
  sceneContentData,
}: {
  headerData: any;
  sceneContentData: any;
  searchStaticAEMData: {
    dropdown: AemSearchDropDown;
    resultsPage: AemSearchResults;
  };
}) => {
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const isSearchQuery = [...searchParams.keys()].includes(
    GLOBAL_SEARCH_QUERY_PARAM_KEY
  );
  const router = useRouter();
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const NoHeaderPage = ["/maintenance"];
  const mobileMedia: number = useMediaQuery() || 0;
  const isMobile = mobileMedia && mobileMedia <= 768.99;
  const { scene, uid } = useAppSelector((state) => state.gigyaslice);
  const mulesoftOffers = useAppSelector((state) => state?.offersSlice);
  const [isOfferLoaded, setIsOfferLoaded] = useState(false);
  const [emailLoginCard, setEmailLoginCard] = useState<string | null>(null);
  const [mulesoftPendingOffers, setMulesoftPendingOffers] = useState<any[]>([]);
  const isHash = [...searchParams.keys()].includes("hash");
  const hashValue = searchParams.get("hash") ?? "";
  const [cardStatus, setCardStatus] = useState("");
  const { favOffers } = useAppSelector((state) => state.favoirateOffersSlice);
  useInputIntent();

  const gigyaToken = useGigyaAccountInfo();

  const getToken = async () => {
    const result = await gigyaToken();
    return result?.idToken ?? "";
  };
  const enrollmentStatus = useMemo(() => {
    return scene?.enrollmentStatus ? scene?.enrollmentStatus : cardStatus;
  }, [scene, cardStatus]);

  const cardNumber = useMemo(() => {
    return isHash ? emailLoginCard : scene?.cardNumber;
  }, [isHash, emailLoginCard, scene?.cardNumber]);

  const allOffersService = async () => {
    try {
      const token = !isHash ? await getToken() : "";
      const allOffersResponse = await fetchMulesoftOffers({
        key: "allOffers",
        cardNumber,
        token,
        isHash,
      });

      // basket builder offer automatically added to fav list
      if (allOffersResponse?.response?.activeOffers.length > 0) {
        const allOffers = [
          ...allOffersResponse?.response?.activeOffers,
          ...allOffersResponse?.response?.pendingOffers,
        ];
        const basketBuilderOFffers = allOffers?.filter(
          (elem: any) =>
            BB_OFFER_TYPE.includes(parseInt(elem.offerType)) &&
            elem?.bannerInd?.includes(process.env.NEXT_PUBLIC_BANNER)
        );
        const bbData = basketBuilderOFffers.map((item: any) => ({
          offer_id: item.offerId,
          banner: item.bannerInd,
          offer_type: item.offerType,
          offer_description: JSON.stringify(item),
        }));
        const finalOffers: any = [];
        const offerIds = new Set(
          favOffers?.basketBuilderOffers?.map((elem: any) => elem.offer_id)
        );

        bbData.forEach((offer: any) => {
          if (!offerIds.has(offer.offer_id)) {
            finalOffers.push(offer);
          }
        });

        if (finalOffers?.length > 0) {
          addOffersToList(finalOffers, token);
        }
      }
      return allOffersResponse;
    } catch (error) {
      console.error("Error fetching Mulesoft offers:", error);
    }
  };

  const loadOffersService = async (pendingOffers: any) => {
    try {
      await mulesoftLoadOffersService({
        key: "loadOffers",
        cardNumber,
        userId: (uid as string) ?? "member",
        offers: pendingOffers,
        token: !isHash ? await getToken() : "",
        isHash: isHash,
      });
    } catch (error) {
      console.error("Error loadOffers:", error);
    }
  };

  const memberInquireService = async ({
    cardStatus,
  }: {
    cardStatus: string;
  }) => {
    try {
      dispatch(setLoadingOffers(true));
      if (isHash) {
        const inquireResponse: any = await mulesoftMemberInquireService({
          key: "memberInquire",
          cardNumber,
          token: "",
          isHash: isHash,
        });
        if (inquireResponse?.response?.enrollmentStatus) {
          setCardStatus(inquireResponse?.response?.enrollmentStatus);
        }
      }
      if (cardStatus !== "LP00") {
        const allOffersResponse = await allOffersService();
        // Ensure pendingOffers is an array
        const pendingOffersList =
          allOffersResponse?.response?.pendingOffers.filter((offer: any) =>
            offer?.bannerInd?.includes(process.env.NEXT_PUBLIC_BANNER)
          ) ?? [];

        if (Array.isArray(pendingOffersList) && pendingOffersList.length > 0) {
          const pendingOffers = pendingOffersList.map((offer) => ({
            id: offer.id,
            channel: isHash ? "001" : "004",
            banner: offer?.bannerInd ?? "MYO",
          }));
          setMulesoftPendingOffers(pendingOffers);
          await loadOffersService(pendingOffers);
          setIsOfferLoaded(true);
        }
        if (allOffersResponse?.response) {
          dispatch(
            setLoadedOffers({
              response: {
                activeOffers: [
                  ...pendingOffersList,
                  ...allOffersResponse?.response?.activeOffers,
                ],
                expiredOffers: [...allOffersResponse?.response?.expiredOffers],
                redeemedOffers: [
                  ...allOffersResponse?.response?.redeemedOffers,
                ],
              },
            })
          );
          if (isHash) {
            dispatch(setHashOffers(true));
          }
        } else {
          dispatch(setLoadedOffers(allOffersResponse));
        }
      }
      dispatch(setLoadingOffers(false));
    } catch (error) {
      console.error("Error fetching Mulesoft offers:", error);
      dispatch(setLoadingOffers(false));
    }
  };
  useEffect(() => {
    if (!isHash && mulesoftOffers?.isHash) {
      dispatch(resetLoadedOffers());
    }
  }, [searchParams]);

  useEffect(() => {
    const dispatchFavOffers = async () => {
      let favOffers;
      if (uid && cardNumber) {
        const data = await getOffersFromList({
          preferredLanguage: "en",
          token: await getToken(),
        });
        favOffers = data?.response;
        // to check redeemed offers and update out database accordingly
        const redeemedOffers = mulesoftOffers?.redeemedOffers;
        const expiredOffers = mulesoftOffers?.expiredOffers;
        if (redeemedOffers?.length > 0 || expiredOffers?.length > 0) {
          favOffers = favOffers.basketBuilderOffers.concat(
            favOffers.favouriteOffers
          );
          const favOffersId = favOffers?.map((r: any) => r.offer_id);
          const reedemedOffersId = redeemedOffers?.map((r: any) => r.offerId);
          const expiredOffersId = expiredOffers?.map((r: any) => r.offerId);

          // Combine both redeemed and expired IDs
          const allOfferIds = [...reedemedOffersId, ...expiredOffersId];

          const commonOfferIds = [
            ...new Set(
              favOffersId.filter((id: any) => allOfferIds?.includes(id))
            ),
          ];

          commonOfferIds.forEach(async (elem: any) => {
            deleteOffersFromList({
              offer_id: elem,
              token: await getToken(),
            });
          });

          const data = await getOffersFromList({
            preferredLanguage: "en",
            token: await getToken(),
          });
          favOffers = await data?.response;
        }
      }
      const favOfferIds = new Set(
        favOffers?.favouriteOffers?.map((item: any) => item.offer_id)
      );
      const filteredOffers = mulesoftOffers?.activeOffers?.filter((item) =>
        favOfferIds.has(item.offerId)
      );
      const filteredfav_offers = filteredOffers.map((itm) => {
        return {
          offer_id: itm.offerId,
          banner: itm.banner,
          offer_type: itm.offerType,
          uid: itm.uid,
          offer_description: JSON.stringify(itm),
        };
      });
      dispatch(
        setFavoirateOffers({
          favouriteOffers: filteredfav_offers,
          basketBuilderOffers: favOffers?.basketBuilderOffers,
        })
      );
    };

    dispatchFavOffers();
  }, [scene?.cardNumber, mulesoftOffers?.activeOffers]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    const handleLogin = async () => {
      if (isHash) {
        const emailRes = await handleEmailLogin(hashValue, isHash);
        if (emailRes?.cardNumber) {
          setEmailLoginCard(emailRes?.cardNumber);
        } else {
          dispatch(
            setAlert({
              message:
                "The link has expired, please sign in to view your current offers or contact Scene+ support at 1-866-586-2805.",
              color: "error",
              isError: true,
            })
          );
          timer = setTimeout(() => {
            router.push("/");
          }, 3000);
        }
      }
    };
    handleLogin();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
      if (cardNumber) {
        await memberInquireService({
          cardStatus: enrollmentStatus,
        });
      } else {
        dispatch(setLoadingOffers(false));
      }
    };
    fetchOffers();
  }, [cardNumber]);

  useEffect(() => {
    const getData = async () => {
      try {
        const items = sceneContentData?.data?.imageAndContentModelList?.items;
        const cookieResetFromAEM = items?.[0]?.cookieReset;
        const cartResetFromAEM = items?.[0]?.cartReset;
        if (cookieResetFromAEM) {
          const cookieValue = getCookie(COOKIE_RESET_KEY);

          if (cookieValue !== cookieResetFromAEM) {
            const allCookies: any = getCookies();
            Object.keys(allCookies).forEach(async (key) => {
              await deleteCookie(key);
            });
            localStorage.clear();
            sessionStorage.clear();

            await setCookie(COOKIE_RESET_KEY, cookieResetFromAEM, {
              maxAge: 60 * 60 * 24 * 365 * 3,
            });
          }
        }

        // Handle Cart Reset via destroyCartDB()
        if (cartResetFromAEM) {
          const storedCartResetVersion = localStorage.getItem(CART_RESET_KEY);
          if (storedCartResetVersion !== cartResetFromAEM) {
            await destroyCartDB();
            localStorage.setItem(CART_RESET_KEY, cartResetFromAEM);
          }
        }
        if (items) {
          dispatch(setSceneContent(items));
        }
      } catch (error) {
        console.error("Error fetching scene content:", error);
      }
    };

    getData();
  }, [dispatch, sceneContentData]);

  return (
    <>
      {!NoHeaderPage.includes(pathname) && (
        <header className="sticky top-0 z-20 w-full left-0">
          <HeaderTopBar />
          <HeaderTopSection
            headerData={headerData}
            isPanelOpen={isPanelOpen}
            setIsPanelOpen={setIsPanelOpen}
            searchStaticAEMData={searchStaticAEMData}
            mobileMedia={mobileMedia}
            isMobile={isMobile}
          />
          <DesktopNavigationBar
            headerData={headerData}
            setIsPanelOpen={setIsPanelOpen}
            mobileMedia={mobileMedia}
            isSearchQuery={isSearchQuery}
            isHomePage={isHomePage}
          />
          <MobileNavigationBar
            headerData={headerData}
            searchStaticAEMData={searchStaticAEMData}
            mobileMedia={mobileMedia}
          />
          {isOfferLoaded && mulesoftPendingOffers?.length > 0 && (
            <ToastComponent autoCloseDuration={900000}>
              <LoadedOffersToast
                mulesoftPendingOffers={mulesoftPendingOffers}
              />
            </ToastComponent>
          )}
        </header>
      )}
    </>
  );
};

export default Header;
